'use client';

import { c as _c } from "react/compiler-runtime";
import { Check, Copy } from 'lucide-react';
import type React from 'react';
import { type MouseEvent, type ReactNode, useCallback } from 'react';
import { useCopyToClipboard } from './use-copy-to-clipboard';
export interface CopyButtonProps {
  /** The string to write to the clipboard. */
  readonly value: string;
  /**
   * Aria-label for the button. Defaults to `"Copy"`. Provide a specific
   * label when the button's surrounding context doesn't name what's being
   * copied (e.g. "Copy meeting ID", "Copy curl command").
   */
  readonly label?: string;
  /** How long the "Copied" flash stays visible, in ms. Default 1500. */
  readonly flashMs?: number;
  /**
   * When true, the icon toggles from `Copy` → `Check` during the flash to
   * give an additional visual cue. Default false — keeps the icon static
   * so screen-reader users, who get the dynamic `title` attribute change,
   * don't have to also hear the icon change.
   */
  readonly toggleIcon?: boolean;
  /** Optional className passed through to the `<button>`. */
  readonly className?: string;
  /**
   * Optional icon override. Sized to `h-3 w-3` by default inside the
   * component — pass a pre-sized element to override.
   */
  readonly icon?: ReactNode;
  /**
   * Invoked after every copy attempt. `success: true` means the
   * clipboard write resolved; `success: false` means it rejected
   * (permission denied, insecure context, document not focused, etc.).
   * The component swallows rejections itself — this callback is for
   * callers that want to surface a fallback UI. Optional.
   */
  readonly onCopyAttempt?: (success: boolean) => void;
}

/**
 * Click-to-copy icon button with a transient "Copied" flash.
 *
 * Writes `value` to the clipboard, sets a `copied` flag for `flashMs`
 * milliseconds, and updates the button's `title` accordingly. The flash
 * timer is stored in a ref and cleared on unmount and on every re-click,
 * so rapid clicks always see a full `flashMs` window and no stale
 * `setCopied(false)` fires on an unmounted component.
 *
 * The click event's propagation is stopped before dispatching to any
 * caller-provided handler, because click-to-copy buttons are routinely
 * nested inside expandable rows / selectable cards whose parent click
 * handlers should NOT fire when the user is just grabbing text off the
 * surface. Every call site in this repo wants this behavior; none rely
 * on bubbling. Callers that genuinely need the click to bubble can wrap
 * the button in a container whose own handler re-dispatches.
 *
 * Clipboard rejections (permission denied, insecure context, document
 * not focused, iframe without `allow="clipboard-write"`) are swallowed
 * — the flash simply does not fire. Callers that need to know can pass
 * `onCopyAttempt(success)` to surface a fallback.
 */
export function CopyButton(t0) {
  const $ = _c(14);
  const {
    value,
    label: t1,
    flashMs,
    toggleIcon: t2,
    className,
    icon,
    onCopyAttempt
  } = t0;
  const label = t1 === undefined ? "Copy" : t1;
  const toggleIcon = t2 === undefined ? false : t2;
  const {
    copied,
    copy
  } = useCopyToClipboard(flashMs);
  let t3;
  if ($[0] !== copy || $[1] !== onCopyAttempt || $[2] !== value) {
    t3 = async event => {
      event.stopPropagation();
      const ok = await copy(value);
      onCopyAttempt?.(ok);
    };
    $[0] = copy;
    $[1] = onCopyAttempt;
    $[2] = value;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleClick = t3;
  let t4;
  if ($[4] !== copied || $[5] !== icon || $[6] !== toggleIcon) {
    t4 = icon ?? (toggleIcon && copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />);
    $[4] = copied;
    $[5] = icon;
    $[6] = toggleIcon;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const displayedIcon = t4;
  const t5 = className ?? "p-0.5 hover:text-foreground transition-colors";
  const t6 = copied ? "Copied" : label;
  let t7;
  if ($[8] !== displayedIcon || $[9] !== handleClick || $[10] !== label || $[11] !== t5 || $[12] !== t6) {
    t7 = <button type="button" onClick={handleClick} className={t5} aria-label={label} title={t6}>{displayedIcon}</button>;
    $[8] = displayedIcon;
    $[9] = handleClick;
    $[10] = label;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}